import { zodResolver } from "@hookform/resolvers/zod";
import { createContext, useContext, useMemo, useState } from "react";
import type { UseFormHandleSubmit, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  SuppressInlineErrors,
  type UseHookFormProps,
  useHookForm,
} from "@/components/base/form/hook-form";
import { cx } from "@/utils/cx";
import type { OpportunityDto } from "@fp/admin-api-client/types/opportunityDto.js";
import { patchOpportunity } from "@fp/admin-api-client/client";
import { HttpStatusCode } from "axios";
import { useFormErrorToasts } from "@/hooks/use-form-error-toasts";
import { ParticipationLimitSection, participationLimitSchemaFields } from "./participation-limit-section";

export const ParticipationFormSchema = z.object({
  id: z.string(),
  ...participationLimitSchemaFields,
}).refine(
  (values) => !values.hasParticipationLimit || (values.maxClaimsPerRoster !== undefined && values.maxClaimsPerRoster !== ""),
  { message: "Participation limit is required", path: ["maxClaimsPerRoster"] },
).refine(
  (values) => !values.hasParticipationLimit || Number(values.maxClaimsPerRoster) >= 1,
  { message: "Participation limit must be at least 1", path: ["maxClaimsPerRoster"] },
);

export type ParticipationFormSchema = z.infer<typeof ParticipationFormSchema>;

export type ParticipationFormContext = {
  form: UseFormReturn<ParticipationFormSchema>;
  handleSubmit: ReturnType<UseFormHandleSubmit<ParticipationFormSchema>>;
  transition: boolean;
  errorMessage: string | undefined;
};

export const ParticipationFormContext = createContext<
  ParticipationFormContext | undefined
>(undefined);

export const DEFAULT_VALUES = {
  id: "",
  hasParticipationLimit: false,
  maxClaimsPerRoster: undefined,
};

export const useParticipationForm = () => {
  const context = useContext(ParticipationFormContext);
  if (!context) {
    throw new Error("ParticipationFormContext not found");
  }
  return context;
};

export type ParticipationFormProps = UseHookFormProps<
  ParticipationFormSchema,
  OpportunityDto
> & {
  defaultValues?: ParticipationFormSchema;
  children:
    | ((context: ParticipationFormContext) => React.ReactNode)
    | React.ReactNode;
};

export const ParticipationForm = ({
  defaultValues = DEFAULT_VALUES,
  onSubmit = ParticipationFormSubmitHandler,
  children,
  ...props
}: ParticipationFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const { form, handleSubmit, transition } = useHookForm<
    ParticipationFormSchema,
    OpportunityDto
  >({
    resolver: zodResolver(ParticipationFormSchema),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
    mode: "onChange",
    onSuccess: async (data) => {
      setErrorMessage(undefined);
      await props.onSuccess?.(data);
    },
    onError: async (errors) => {
      setErrorMessage("Unable to update participation");
      await props.onError?.(errors);
    },
    onSubmit,
    ...props,
  });

  const context = useMemo(
    () => ({ form, handleSubmit, transition, errorMessage }),
    [form, handleSubmit, transition, errorMessage],
  );

  return (
    <ParticipationFormContext.Provider value={context}>
      <Form {...form}>
        {typeof children === "function" ? children(context) : children}
      </Form>
    </ParticipationFormContext.Provider>
  );
};

export const ParticipationFormErrorMessage = () => {
  const { errorMessage } = useParticipationForm();
  if (!errorMessage) return null;
  return <p className="text-error-primary">{errorMessage}</p>;
};

export const ParticipationFormSubmitHandler = async (
  formValues: ParticipationFormSchema,
) => {
  const result = await patchOpportunity(formValues.id, {
    maxClaimsPerRoster: formValues.hasParticipationLimit
      ? Number(formValues.maxClaimsPerRoster)
      : null,
  });

  if (result.status !== HttpStatusCode.Ok) {
    return {
      success: false as const,
      error: new Error("Unable to update opportunity"),
    };
  }

  return { success: true as const, data: result.data };
};

export const ParticipationFormContent = ({
  className,
}: {
  className?: string;
}) => {
  const { form } = useParticipationForm();
  useFormErrorToasts(form.control);
  const hasParticipationLimit = form.watch("hasParticipationLimit");
  return (
    <SuppressInlineErrors>
      <div className={cx("flex flex-col gap-4", className)}>
        <ParticipationLimitSection
          control={form.control}
          fieldNames={{
            hasParticipationLimit: "hasParticipationLimit",
            maxClaimsPerRoster: "maxClaimsPerRoster",
          }}
          hasParticipationLimit={hasParticipationLimit}
          onToggle={() => form.trigger()}
        />
      </div>
    </SuppressInlineErrors>
  );
};
